/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @nx/enforce-module-boundaries */
import { GetServerSideProps } from 'next';

import { PageRendererComponent, prePageCall } from '@marriott/mi-headless-utils';
import { isServer } from '@marriott/mi-apollo-client-utils';
import destinationSignatures from '../../signatures/operationSignaturesDestinations.json';
import { getDestinationRedirects, getSubDirectoryLocale } from '@marriott/mi-leisure-components/utils';
import { PARAM_PAGENO } from '@marriott/mi-leisure-components/constants';
import { NEXT_PUBLIC_ENV_KEYS } from '@marriott/mi-leisure-components/constants';
import moment from 'moment';
import {
  getUserAgentType,
  getProcessedData,
  getSearchResultQuery,
  getSortByOptions,
  getSortByVal,
} from 'libs/mi-leisure-components/src/organisms/PropertiesList/utils/helper';
import { FEATURE_FLAGS, fetchFlag, TARGETS } from '@marriott/mi-feature-flag-utils';

const { ENABLE_DESTINATION_REDIRECTS, REDIRECT_URL_DESTINATION, LEISURE_APP_DOMAIN, SESSION_APP_DOMAIN_URL } =
  process.env;

function pageRenderer(props) {
  console.log('[destination] props ', props);
  const url =
    props.model && props.model['canonical'] !== undefined && props.model['canonical'] !== ''
      ? new URL(props.model['canonical'], props.model['canonical'])
      : ''; // Base URL needed to parse relative URL
  const param = PARAM_PAGENO;
  if (url !== '' && url.searchParams.has(param)) {
    if (props.pageNo === '1') {
      url.searchParams.delete(param);
    } else {
      url.searchParams.set(param, props.pageNo); // Update the parameter value
    }
  } else {
    if (url !== '' && props.pageNo != null && props.pageNo !== '1') {
      url.searchParams.append(param, props.pageNo); // Add it if it doesn't exist
    }
  }
  const baseUrl = url.toString();
  props.model['canonical'] = baseUrl;
  return <PageRendererComponent {...props} />;
}

const GetDestinationJson = async () => {
  if (isServer) {
    const DestinationsValues = await import('../../mocks/destinations-redirect-es.json');
    return DestinationsValues;
  }
  return {};
};

//Any product specific page data apart from common data like session, datalayer
//can be returned from this function and the same is passed as props to the page Component
const destinationSpecificData = async (context: any, parallelCallsResponse: any, interceptorResponse: any) => {
  console.log('[destinationSpecificData] offers context -', context);
  console.log('[destinationSpecificData page] parallelCallsResponse ', parallelCallsResponse);
  const currentLocale = interceptorResponse?.requestAttributes?.pageManagerRequest?.global?.localeKey?.replace(
    '_',
    '-'
  );
  const { req, res } = context;
  let pageNo = null;

  const isAuthorMode = req?.url?.includes('isAuthorMode');

  if (!isAuthorMode) {
    const url = new URL(req.url, `http://${req.headers.host}`); // Use the full URL
    //capturing query param pageNO from req url
    pageNo = url.searchParams.get(PARAM_PAGENO);
  }

  // Function to handle locale mapping, converting 'en-us' to 'en'
  const normalizeLocale = (locale: string) => (locale.includes('en-us') ? 'en' : locale);

  // Get the normalized locale and construct the redirect URL
  const normalizedLocale = normalizeLocale(getSubDirectoryLocale(currentLocale));
  const redirectUrlWithSubdirectory = `/${normalizedLocale}${REDIRECT_URL_DESTINATION}`;
  console.log('redirectUrlWithSubdirectory in phx destination handler ', redirectUrlWithSubdirectory);

  // Feature flag call for logo automation
  const featureFlagLogo = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_LOGO_AUTOMATION, {
    target: TARGETS.APP.OFFERS_RENDERER,
  });

  const destinationsRedirectFlag = ENABLE_DESTINATION_REDIRECTS === 'true';
  if (req?.headers?.['leisure-headless'] === 'true') {
    const destinationvalues = await GetDestinationJson();
    if (req?.url?.includes('/destinations') && destinationsRedirectFlag && !isAuthorMode) {
      getDestinationRedirects(req, destinationvalues, currentLocale, res);
    }
  }
  return {
    operationSignatures: destinationSignatures,
    redirectUrI: redirectUrlWithSubdirectory,
    pageNo: pageNo,
    nextPublicEnvKeys: NEXT_PUBLIC_ENV_KEYS,
    featureFlags: { featureFlagLogo },
  };
};

const postAEMModelOperations = async (
  context: any,
  parallelCallsResponse: any,
  interceptorResponse: any,
  model: any
) => {
  const viewPort = getUserAgentType(context?.req?.headers?.['user-agent']);
  const { req, query } = context;
  const sessionID = req?.cookies?.sessionID ?? '';
  const requestId = req.headers['x-request-id'] ?? `${Date.now()}`;
  const headersData = req?.headers;
  const isDTT = headersData.dtt === 'true' || query.dtt === 'true';
  const resolvedUrl = context?.resolvedUrl;
  const { sessionData } = parallelCallsResponse;
  const currentLocale = interceptorResponse?.requestAttributes?.pageManagerRequest?.global?.localeKey?.replace(
    '_',
    '-'
  );
  let serverObj = {};
  const ariesOffer = sessionData?.cacheData?.data?.AriesOffer;
  const ariesSessionDestination = ariesOffer?.AriesSessionDestination;
  const countryCode = model?.destinationMetaData?.countryCode;
  const taxFlag = currentLocale === 'en-US' && countryCode != 'GB' && countryCode != 'JP' ? false : true;

  // should compare full path.
  const cleanedPath = resolvedUrl?.split('?')[0].replace(/\.mi$/, '');
  const pathWithLocale = `/${currentLocale}${cleanedPath}`;
  const currentDestination = currentLocale ? pathWithLocale.toLocaleLowerCase() : cleanedPath;
  console.log('current destination >> ', currentDestination, ' <<>> ', ariesSessionDestination);
  const hasdestinationChanged = !(currentDestination === ariesSessionDestination);
  const hasDefaultFilters =
    model?.destinationMetaData?.amenities?.length ||
    model?.destinationMetaData?.activities?.length ||
    model?.destinationMetaData?.hotelType?.length;
  const sortBySessionVal = ariesOffer?.['sortBy'];
  const sortBy = hasdestinationChanged
    ? getSortByVal('', model?.destinationMetaData)
    : getSortByVal(sortBySessionVal, model?.destinationMetaData);
  const isFlexibleDateSearch = false;
  const items = model.cqItems?.root?.[':items']?.responsivegrid?.[':items'];
  const propertiesListModel = Object.keys(items)
    .map(key => items[key])
    .find(component => component[':type'] === 'mi-aem-leisure-spa/components/propertiesList');
  const cardsCount = model?.propertiesPerPage ? Number(model?.propertiesPerPage) : 8;
  let page = 1;
  let serverSideoffset = 0;
  let pageNo = null;
  const url = new URL(req?.url, `http://${req?.headers?.host}`); // Use the full URL
  //capturing query param pageNO from req url
  pageNo = url.searchParams.get(PARAM_PAGENO);
  const baseURL = model?.canonical;
  page = parseInt(pageNo ? pageNo : '1');
  const currentPage = page;
  serverSideoffset = (page - 1) * cardsCount;
  const isStateOrCountryEnabled =
    model?.destinationMetaData?.isCountryEnabled || model?.destinationMetaData?.isStateEnabled;

  const normalizedLocale = model?.subDirectoryContextPath;
  const operationName = isStateOrCountryEnabled
    ? isDTT
      ? 'phoenixLeisureSearchLowestAvailableRatesByLocation'
      : 'phoenixLeisureDestinationPropertiesByLocation'
    : isDTT
    ? 'phoenixLeisureSearchLowestAvailableRatesByGeolocation'
    : 'phoenixLeisureDestinationPropertiesByGeolocation';

  let response = {};
  let serverApiError: string | null = null;
  // update sessionData utility server side
  const updateSessionObjfunc = async (ariesSearch: any = {}) => {
    try {
      const url = `${SESSION_APP_DOMAIN_URL}/phoenix-gateway/v1/updateSession`;
      const headers = {
        Cookie: `sessionID=${sessionID}`,
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json',
      };
      const payload = {
        AriesSearch: ariesSearch,
        remove: [],
      };
      await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload),
      })
        .then(res => res.json())
        .then(data => {
          return data?.status;
        });
    } catch (error) {
      console.log(`[DESTINATIONS] update session API Failed:, ${error}`);
    }
  };
  //update ariesOffer session with selected filters
  const updateSessionObj = async (ariesOffer: any = {}) => {
    const sessionToken = sessionData?.sessionToken;
    try {
      const url = `${SESSION_APP_DOMAIN_URL}/phoenix-gateway/v1/updateSession`;
      const headers = {
        Cookie: `sessionID=${sessionToken}`,
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json',
      };
      const payload = {
        AriesOffer: ariesOffer,
        remove: [],
      };
      await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload),
      })
        .then(res => res.json())
        .then(data => {
          console.log(data, 'OFFER OBJECT UPDATED');
          return data?.status;
        });
    } catch (error) {
      console.log(`[DESTINATIONS] update session API Failed:, ${error}`);
    }
  };
  const updateSessionData = async (destinationMetaData: any) => {
    const today = moment().utc().startOf('day').toISOString();
    const tomorrow = moment().utc().add(1, 'days').startOf('day').toISOString();
    let ariesSearch: any = sessionData?.cacheData?.data?.AriesSearch;
    ariesSearch = {
      ...ariesSearch,
      searchCriteria: {
        ...ariesSearch?.searchCriteria,
        address: {
          city:
            (!(destinationMetaData?.isCountryEnabled || destinationMetaData?.isStateEnabled)
              ? destinationMetaData?.city
              : '') || '',
          country: destinationMetaData?.countryCode || '',
          destination: destinationMetaData?.destinationName || '',
          latitude:
            (!(destinationMetaData?.isCountryEnabled || destinationMetaData?.isStateEnabled)
              ? parseFloat(destinationMetaData?.latitude)
              : '') || '',
          longitude:
            (!(destinationMetaData?.isCountryEnabled || destinationMetaData?.isStateEnabled)
              ? parseFloat(destinationMetaData?.longitude)
              : '') || '',
          stateProvince: (destinationMetaData?.isCountryEnabled ? '' : destinationMetaData?.stateCode) || '',
          stateProvinceDisplayName: (destinationMetaData?.isCountryEnabled ? '' : destinationMetaData?.stateCode) || '',
          destinationType: destinationMetaData?.isCountryEnabled
            ? 'NA'
            : destinationMetaData?.isStateEnabled
            ? 'State/Province/Region'
            : 'City',
        },
        countryName: destinationMetaData?.country || '',
        destinationAddressPlaceId: destinationMetaData?.placeId || '',
        geoCode: !(destinationMetaData?.isCountryEnabled || destinationMetaData?.isStateEnabled)
          ? `${destinationMetaData?.latitude}, ${destinationMetaData?.longitude}`
          : '',
        availabilityRequestVO: {
          ...ariesSearch?.searchCriteria?.availabilityRequestVO,
          checkInDate: today,
          checkOutDate: tomorrow,
          flexibleDate: isFlexibleDateSearch,
          lengthOfStay: 1,
          flexibleDateSearchRateDisplay: false,
          hideFlexibleDateCalendar: false,
          numAdultsPerRoom: 1,
          numChildrenPerRoom: 0,
          numGuestsPerRoom: 1,
          numRooms: 1,
        },
      },
      search_date_type: isFlexibleDateSearch ? 'Flexible Dates' : 'With Dates',
      search_dates_flexible: isFlexibleDateSearch,
    };
    await updateSessionObjfunc(ariesSearch);
    if (hasdestinationChanged) {
      let ariesOffer: any = sessionData?.cacheData?.data?.AriesOffer;
      ariesOffer = {
        ...ariesOffer,
        selectedBrands: [],
        selectedAmenities:
          hasDefaultFilters && model?.destinationMetaData?.amenities?.length > 0
            ? [
                {
                  code: model?.destinationMetaData?.amenities?.[0],
                },
              ]
            : [],
        selectedTransportaion: [],
        selectedHotelType:
          hasDefaultFilters && model?.destinationMetaData?.hotelType?.length > 0
            ? [
                {
                  code: model?.destinationMetaData?.hotelType?.[0],
                },
              ]
            : [],
        selectedEvents: [],
        selectedActivities:
          hasDefaultFilters && model?.destinationMetaData?.activities?.length > 0
            ? [
                {
                  code: model?.destinationMetaData?.activities?.[0],
                },
              ]
            : [],
        selectedPrices: [
          {
            label: propertiesListModel?.anyPriceLabel,
            disabled: false,
          },
        ],
        // selectedCurrency: [
        //   {
        //     label: propertiesListModel?.defaultLabel ?? 'Default',
        //     code: 'USD',
        //     disabled: false,
        //   },
        // ],
        // currencyConversionRate: 1,
        selectedCity: [],
        selectedState: [],
        includeUnavailableProperties: true,
        showPricesTax: taxFlag,
        sortBy: sortBy,
      };
      serverObj = ariesOffer;
      console.log(ariesOffer, 'ariesOffer constructed from Server side');
      await updateSessionObj(ariesOffer);
    }
  };
  await updateSessionData(model?.destinationMetaData);
  // get property data on server side

  //fetch propertiesList details service
  const getDestinationPropertyDetails = async () => {
    try {
      const url = `${LEISURE_APP_DOMAIN}/destinations/getProperties`;
      const headers = {
        'Content-Type': 'application/json',
        'X-Request-Id': requestId ?? '',
        'Accept-Language': currentLocale ?? 'en-US',
        sessionToken: sessionID ?? '',
      };
      const formatDate = (date: any) => date.toISOString().split('T')[0];
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      const startDate = formatDate(today);
      const endDate = formatDate(tomorrow);
      const searchData = {
        startDate: startDate,
        endDate: endDate,
        childAges: [],
        numberInParty: 1,
        quantity: 1,
      };
      const variables = getSearchResultQuery(
        searchData,
        model?.destinationMetaData,
        0,
        [],
        cardsCount,
        sortBy,
        [],
        [],
        [],
        [],
        [],
        [],
        [],
        [],
        true,
        true,
        currentLocale,
        true,
        serverSideoffset,
        ariesOffer,
        countryCode,
        hasdestinationChanged
      );
      const body = {
        sessionToken: sessionID ?? '',
        queryName: operationName,
        variables: variables,
      };
      const res = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        serverApiError = res?.statusText;
        console.log(`[Destination PropertiesList] API Failed with status: ${res?.status}`);
      }
      const data = await res.json();
      console.log(`[Destination PropertiesList] API response: ${JSON.stringify(data)}`);
      response = data?.data ?? { test: 'test data' };
    } catch (error) {
      console.log(`[DESTINATIONS PropertiesList] API Failed:, ${error}`); //ONERROR BLOCK
    }
    return response;
  };
  const serverSideUXLData = await getDestinationPropertyDetails();

  const sortOptions = getSortByOptions(propertiesListModel, isStateOrCountryEnabled);

  const processedData = getProcessedData(
    serverSideUXLData,
    isStateOrCountryEnabled,
    propertiesListModel,
    normalizedLocale,
    currentLocale,
    currentPage,
    countryCode,
    ariesOffer,
    isDTT,
    viewPort
  );
  const sortByData = {
    sortOptions: sortOptions,
    sortBy: sortBy,
    destinationMetaData: model?.destinationMetaData,
  };
  const paginationPanelLabel = {
    previous: propertiesListModel?.previous,
    next: propertiesListModel?.next,
  };
  const paginationData = {
    currentPage: currentPage,
    cardsCount: cardsCount,
    paginationPanelLabel: paginationPanelLabel,
  };
  // update models as per the requirments
  Object.keys(items).forEach(key => {
    const component = items[key];
    if (component[':type'] === 'mi-aem-leisure-spa/components/leisure-searchform') {
      component.numberOfSearchForms = 1;
      component.enableRedeemPoints = false;
      component.disableSpecialRates = true;
      component.disableRoomGuest = true;
    }
    if (component[':type'] === 'mi-aem-leisure-spa/components/propertiesList') {
      component.processedData = processedData || {};
      component.sortByData = sortByData || {};
      component.isServerSideData = true;
      component.paginationData = paginationData || {};
      component.baseURL = baseURL;
      component.serverApiError = serverApiError;
      component.viewPort = viewPort;
      component.hasdestinationChanged = hasdestinationChanged;
      component.taxFlag = taxFlag;
      component.sessionFromServer = serverObj;
    }
  });
  return model;
};
//Make pre page calls like session, datalayer, fetch model.json, producSpecifc data etc
//at server side and pass the data is passed as props to page component.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getServerSideProps: GetServerSideProps<any> = prePageCall(
  destinationSpecificData,
  {},
  false,
  false,
  false,
  undefined,
  postAEMModelOperations
);
export default pageRenderer;
